import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Plus, CalendarDays, Loader2, ScanLine, Camera, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";
import BarcodeScanner from "@/components/BarcodeScanner";

export default function AddProductDialog({ categories, onSaved, product, open: openProp, onOpenChange }) {
  const { t } = useTranslation();
  const isEdit = !!product;
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? openProp : openState;
  const setOpen = (o) => { if (controlled) onOpenChange?.(o); else setOpenState(o); };

  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [category, setCategory] = useState("Autre");
  const [price, setPrice] = useState("");
  const [expiry, setExpiry] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [scanned, setScanned] = useState(null); // { image, brand, barcode }
  const [genBusy, setGenBusy] = useState(false);
  const fileRef = useRef(null);

  const generateImage = async () => {
    if (!name.trim()) {
      toast.error("Indiquez d'abord le nom du produit.");
      return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post("/products/generate-image", {
        name: name.trim(),
        category,
      });
      setScanned({ image: data.image, brand: "", barcode: "" });
      toast.success("Image générée ✨");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenBusy(false);
    }
  };

  const handleOcrFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setOcrBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post("/products/ocr-date", { image_base64: dataUrl });
      if (data.found && data.date) {
        try { setExpiry(parseISO(data.date)); } catch (_) { /* ignore */ }
        toast.success("Date de péremption détectée — vérifiez-la.");
      } else {
        toast.info("Date non détectée — saisissez-la manuellement.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOcrBusy(false);
    }
  };

  const handleScan = (data) => {
    if (data.name) setName(data.name);
    if (data.quantity) setQuantity(data.quantity);
    if (data.category) setCategory(data.category);
    if (data.suggested_expiry) {
      try { setExpiry(parseISO(data.suggested_expiry)); } catch (_) { /* ignore */ }
    }
    if (data.image || data.brand || data.barcode) {
      setScanned({ image: data.image || "", brand: data.brand || "", barcode: data.barcode || "" });
    }
    if (data.found && data.suggested_expiry) {
      toast.info("Date de péremption suggérée — ajustez-la si besoin.");
    }
  };

  const reset = () => {
    setName(""); setQuantity(""); setCategory("Autre"); setPrice(""); setExpiry(null); setNotify(true); setScanned(null);
  };

  useEffect(() => {
    if (open && isEdit) {
      setName(product.name || "");
      setQuantity(product.quantity || "");
      setCategory(product.category || "Autre");
      setPrice(product.price != null ? String(product.price) : "");
      setExpiry(product.expiry ? parseISO(product.expiry) : null);
      setNotify(product.notify !== false);
      if (product.image) setScanned({ image: product.image, brand: "", barcode: "" });
    }
  }, [open, isEdit, product]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !expiry) {
      toast.error("Indiquez au moins un nom et une date.");
      return;
    }
    setBusy(true);
    const payload = {
      name: name.trim(),
      quantity: quantity.trim(),
      category,
      price: price === "" ? null : parseFloat(price),
      expiry: format(expiry, "yyyy-MM-dd"),
      notify,
      image: scanned?.image || (isEdit ? product?.image || null : null),
    };
    try {
      if (isEdit) {
        const { data } = await api.put(`/products/${product.id}`, payload);
        toast.success(`« ${data.name} » mis à jour.`);
        setOpen(false);
        onSaved?.(data);
      } else {
        const { data } = await api.post("/products", payload);
        toast.success(`« ${data.name} » ajouté à la liste.`);
        reset();
        setOpen(false);
        onSaved?.(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && !isEdit) reset(); }}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button className="rounded-full h-11 sm:h-10 w-full sm:w-auto" data-testid="open-add-product-btn">
            <Plus className="h-4 w-4 mr-1.5" /> {t("dashboard.add")}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? t("product.titleEdit") : t("product.titleNew")}</DialogTitle>
          <DialogDescription>
            {isEdit ? t("product.descEdit") : t("product.descNew")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-product-form">
          {!isEdit && (
            <Button type="button" variant="outline"
              className="w-full rounded-full border-dashed border-primary/40 text-primary hover:bg-primary/5"
              onClick={() => setScanOpen(true)} data-testid="scan-barcode-btn">
              <ScanLine className="h-4 w-4 mr-2" /> {t("product.scanBarcode")}
            </Button>
          )}
          {scanned && (scanned.image || scanned.brand || scanned.barcode) && (
            <div className="flex items-center gap-3 rounded-2xl border border-primary/25 bg-primary/[0.04] p-3" data-testid="scanned-preview">
              {scanned.image ? (
                <img
                  src={scanned.image}
                  alt=""
                  loading="lazy"
                  className="h-16 w-16 rounded-xl object-cover bg-secondary shrink-0"
                  data-testid="scanned-image"
                />
              ) : (
                <div className="h-16 w-16 rounded-xl bg-secondary shrink-0 flex items-center justify-center">
                  <ScanLine className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs mono uppercase tracking-mono text-primary">{t("product.productRecognized")}</p>
                <p className="font-semibold truncate">{scanned.brand || t("product.noBrand")}</p>
                {scanned.barcode && <p className="mono text-[10px] text-muted-foreground truncate">{scanned.barcode}</p>}
              </div>
              <button
                type="button"
                onClick={() => setScanned(null)}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-full"
                aria-label="Remove"
                data-testid="scanned-clear-btn"
              >
                ×
              </button>
            </div>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="p-name">{t("product.name")}</Label>
              {!scanned && name.trim() && (
                <button
                  type="button"
                  onClick={generateImage}
                  disabled={genBusy}
                  data-testid="generate-image-btn"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-60"
                >
                  {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {t("product.generateImage")}
                </button>
              )}
            </div>
            <Input id="p-name" data-testid="product-name-input" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Yaourts nature" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="p-qty">{t("product.quantity")}</Label>
              <Input id="p-qty" data-testid="product-qty-input" value={quantity}
                onChange={(e) => setQuantity(e.target.value)} placeholder="x4" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-price">{t("product.price")}</Label>
              <Input id="p-price" data-testid="product-price-input" type="number" step="0.01" min="0"
                value={price} onChange={(e) => setPrice(e.target.value)} placeholder="2.50" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("product.category")}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="product-category-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>{t("product.expiresOn")}</Label>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={ocrBusy}
                data-testid="ocr-date-btn"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-60">
                {ocrBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                {t("product.photoDate")}
              </button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment"
                className="hidden" onChange={handleOcrFile} data-testid="ocr-file-input" />
            </div>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" type="button" data-testid="product-expiry-trigger"
                  className={cn("w-full justify-start rounded-full font-normal", !expiry && "text-muted-foreground")}>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  {expiry ? format(expiry, "d MMMM yyyy", { locale: fr }) : t("product.chooseDate")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card" align="start">
                <Calendar mode="single" selected={expiry} onSelect={(d) => { setExpiry(d); setDateOpen(false); }} locale={fr} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-border px-3 py-2.5">
            <div>
              <p className="text-sm font-medium">{t("product.reminderEmail")}</p>
              <p className="text-xs text-muted-foreground">{t("product.reminderSub")}</p>
            </div>
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="product-notify-switch" />
          </div>

          <DialogFooter>
            <Button type="submit" className="rounded-full w-full" disabled={busy} data-testid="submit-product-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? t("product.save") : t("product.addToList")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {!isEdit && (
      <BarcodeScanner open={scanOpen} onOpenChange={setScanOpen} onResult={handleScan} />
    )}
    </>
  );
}
